import React from "react";
import { Helmet } from "react-helmet";
import { baseUrl } from "../config";

const SEOHead = ({
  title = "GeniusNexa - Transform Your Business with AI & Digital Innovation",
  description = "Leading digital transformation partner specializing in AI, cloud solutions, and enterprise software development. Drive growth with proven expertise and innovative technology solutions.",
  keywords = "AI solutions, digital transformation, cloud computing, enterprise software, business automation, data analytics, machine learning, software development, consulting services",
  image = `${baseUrl}/og-image.jpg`,
  url = baseUrl,
  type = "website",
  author = "GeniusNexa",
  publishedTime,
  modifiedTime,
  section,
  tags = [],
  noindex = false,
  canonical,
}) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "GeniusNexa",
    url: baseUrl,
    logo: `${baseUrl}/logo.png`,
    description: "Digital transformation and AI solutions company",
    address: {
      "@type": "PostalAddress",
      addressCountry: "US",
    },
    sameAs: [
      "https://www.linkedin.com/company/geniusnexa",
      "https://twitter.com/geniusnexa",
      "https://github.com/geniusnexa",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      email: "support@geniusnexa.com",
      availableLanguage: ["English"],
    },
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: baseUrl,
      },
    ],
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="GeniusNexa" />
      <meta property="og:locale" content="en_US" />
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {section && <meta property="article:section" content={section} />}
      {tags.length > 0 &&
        tags.map((tag, idx) => (
          <meta key={idx} property="article:tag" content={tag} />
        ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:creator" content="@geniusnexa" />

      {/* Additional Meta Tags */}
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=5"
      />
      <meta name="theme-color" content="#0b1220" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="format-detection" content="telephone=no" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>

      {/* Preconnect to external resources */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />

      {/* Favicon */}
      <link rel="icon" type="image/svg+xml" href="/images/logo-icon.svg" />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-touch-icon.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/images/logo-icon.svg"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/images/logo-icon.svg"
      />
      <link rel="manifest" href="/site.webmanifest" />
    </Helmet>
  );
};

export default SEOHead;
